import lodash from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import { Editor } from 'react-draft-wysiwyg';

import withDraggable from '~app/core/withDraggable';
import { useT } from '~app/core/BundleProvider';
import { actions as CanvasActions } from '~app/features/canvas';
import { actions as DocumentActions } from '~app/features/document';

import withEnterToClick from '~app/core/withEnterToClick';
import EditableLabel from './EditableLabel';
import { MAX_NAME_LENGTH } from '../consts';

const ClickOrEnterDiv = withEnterToClick('div');

function InstructionsBox({
  description,
  title,
  maxHeight,
  selected,
  labelFocus,
}) {
  const dispatch = useDispatch();
  const t = useT();
  const instructionsBox = useSelector(state => state.canvas.instructionsBox);
  const editMode = useSelector(state => state.document.present?.editMode);
  // controlled editor state:
  const [editorState, setEditorState] = useState(null);
  // markdown content; normally tracks the description property:
  const [editorContent, setEditorContent] = useState(null);
  // instruction box header content; tracks the instruction box header property:
  const [instructionHeaderContent, setInstructionHeaderContent] = useState(
    title,
  );

  useEffect(() => {
    // The description property will differ from the local copy of
    // the content when the component loads, or when the description
    // is updated in Redux through some other action (e.g. undo).
    // In those cases, we need to update the editor's content to
    // reflect the change via Redux, which is done here by re-
    // initializing the editor state.
    if (description !== editorContent) {
      setEditorState(
        EditorState.createWithContent(
          convertFromRaw(markdownToDraft(description)),
        ),
      );
      setEditorContent(description);
    }
  }, [dispatch, description, editorContent, setEditorContent]);

  const toggleOnClick = useCallback(
    e => {
      if (instructionsBox.expanded === true) {
        dispatch(CanvasActions.collapseInstructionsBox());
      } else {
        dispatch(CanvasActions.expandInstructionsBox());
      }
    },
    [dispatch, instructionsBox],
  );

  const onEditorStateChange = useCallback(
    e => {
      setEditorState(e);
      const newcontent = e.getCurrentContent();
      const newobject = convertToRaw(newcontent);
      let newval = draftToMarkdown(newobject);
      if (newval === '') {
        //instructions value can't be empty
        newval = ' ';
      }
      if (newval !== editorContent) {
        setEditorContent(newval);
      }
      if (newval !== description) {
        dispatch(DocumentActions.updateInstructions(newval));
      }
    },
    [dispatch, description, setEditorState, editorContent, setEditorContent],
  );

  const onHeaderStateChange = useCallback(
    e => {
      const newval = e;
      if (newval !== instructionHeaderContent) {
        setInstructionHeaderContent(newval);
      }
      if (newval !== title) {
        dispatch(DocumentActions.updateHeader(newval));
      }
    },
    [dispatch, title, instructionHeaderContent, setInstructionHeaderContent],
  );

  const onFocus = useCallback(
    e => {
      // add tabindex for making the content accessible
      // enables it to scroll through the body
      const container = e.target.querySelectorAll('div')[0];
      const matches = container && container.querySelectorAll('div');
      matches &&
        matches.forEach(element => {
          element.setAttribute('tabindex', '0');
        });
      dispatch(CanvasActions.labelFocusOn());
    },
    [dispatch],
  );

  const onBlur = useCallback(() => {
    dispatch(CanvasActions.labelFocusOff());
  }, [dispatch]);

  return (
    <div className={`InstructionsBox__node`}>
      <div tabIndex={0}>
        <div>
          <div
            className={classNames(
              `handle InstructionsBox__header ${selected ? 'selected' : ''}`,
              {
                'InstructionsBox__header--expanded': instructionsBox.expanded,
              },
            )}
          >
            <h2 className="mb-0">
              <EditableLabel
                value={instructionHeaderContent}
                isEditable={!!editMode && selected}
                onFinalChange={text => onHeaderStateChange(text)}
                maxLength={MAX_NAME_LENGTH}
                labelFocus={labelFocus}
              />
            </h2>
            <ClickOrEnterDiv
              className="InstructionsBox__button"
              onClick={toggleOnClick}
              onTouchStart={toggleOnClick}
              tabIndex={0}
              role="button"
            >
              <img
                src={
                  instructionsBox.expanded
                    ? require('~app/resources/images/icons/up-arrow.svg')
                    : require('~app/resources/images/icons/down-arrow.svg')
                }
                //alt={t('expand-instructions')}
                //A3MG 12/11/23
                alt={
                  instructionsBox.expanded
                    ? t('collapse-instructions')
                    : t('expand-instructions')
                }
                //END A3MG 12/11/23`
              />
            </ClickOrEnterDiv>
          </div>
          {instructionsBox.expanded && (
            <div
              className="InstructionsBox__body"
              style={{ maxHeight: maxHeight - 56 }}
            >
              <label className="InstructionsBoxLabel">Model Instructions</label>
              <Editor
                readOnly={!editMode ? true : null}
                editorState={editorState}
                onEditorStateChange={onEditorStateChange}
                wrapperClassName="InstructionsBox__editor-wrapper"
                toolbarClassName="InstructionsBox__editor-toolbar"
                toolbarOnFocus
                toolbar={{
                  options: ['inline', 'blockType', 'list', 'link'],
                  inline: {
                    options: ['bold', 'italic'],
                    bold: { className: 'InstructionsBox__toolbar-button' },
                    italic: { className: 'InstructionsBox__toolbar-button' },
                  },
                  blockType: {
                    className: 'InstructionsBox__toolbar-button',
                  },
                  list: {
                    unordered: { className: 'InstructionsBox__toolbar-button' },
                    ordered: { className: 'InstructionsBox__toolbar-button' },
                    indent: { className: 'InstructionsBox__toolbar-button' },
                    outdent: { className: 'InstructionsBox__toolbar-button' },
                  },
                  link: {
                    link: { className: 'InstructionsBox__toolbar-button' },
                    unlink: { className: 'InstructionsBox__toolbar-button' },
                  },
                }}
                onFocus={onFocus}
                onBlur={onBlur}
                tabIndex={0}
                ariaLabel="Model Instructions"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// cancel prop - disables the draggable on the div with the classname
export default React.memo(
  withDraggable(InstructionsBox, {
    draggableProps: {
      handle: '.handle',
      defaultClassName: 'InstructionsBox-draggable',
      cancel: '.canvas-object',
    },
  }),
  lodash.isEqual,
);
